(() => {
  const onReady = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  onReady(() => {
    const header = document.querySelector('[data-site-header]');
    const menuToggle = document.querySelector('[data-menu-toggle]');
    const siteNav = document.querySelector('[data-site-nav]');
    const menuLabel = menuToggle?.querySelector('.sr-only');
    const desktopNavigation = window.matchMedia('(min-width: 861px)');

    const setupAnnouncementToggles = (root = document) => {
      root.querySelectorAll('[data-announcement-toggle]').forEach((button) => {
        if (button.dataset.announcementToggleBound === 'true') return;

        const announcement = button.closest('.announcement');
        const label = button.querySelector('.sr-only');
        if (!announcement) return;

        button.dataset.announcementToggleBound = 'true';
        button.addEventListener('click', () => {
          const isPaused = announcement.classList.toggle('is-paused');
          button.setAttribute('aria-pressed', String(isPaused));
          if (label) label.textContent = isPaused ? 'Play announcement ticker' : 'Pause announcement ticker';
        });
      });
    };

    setupAnnouncementToggles();
    document.addEventListener('shopify:section:load', (event) => setupAnnouncementToggles(event.target));

    const updateHeader = () => {
      if (header) header.classList.toggle('is-scrolled', window.scrollY > 8);
    };

    const updateMobileNavigationHeight = () => {
      if (!header) return;
      const headerBottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
      document.documentElement.style.setProperty('--mobile-nav-top', `${headerBottom}px`);
    };

    const closeMenu = () => {
      if (!menuToggle || !siteNav) return;
      menuToggle.setAttribute('aria-expanded', 'false');
      if (menuLabel) menuLabel.textContent = 'Open menu';
      siteNav.classList.remove('is-open');
      document.body.classList.remove('nav-open');
    };

    if (header) {
      updateHeader();
      window.addEventListener('scroll', updateHeader, { passive: true });
    }

    if (menuToggle && siteNav) {
      menuToggle.addEventListener('click', () => {
        const isOpen = menuToggle.getAttribute('aria-expanded') === 'true';
        if (!isOpen) updateMobileNavigationHeight();
        menuToggle.setAttribute('aria-expanded', String(!isOpen));
        if (menuLabel) menuLabel.textContent = isOpen ? 'Open menu' : 'Close menu';
        siteNav.classList.toggle('is-open', !isOpen);
        document.body.classList.toggle('nav-open', !isOpen);
      });

      siteNav.querySelectorAll('a').forEach((link) => link.addEventListener('click', closeMenu));
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeMenu();
      });

      const handleNavigationViewportChange = () => {
        if (desktopNavigation.matches) {
          closeMenu();
        } else if (menuToggle.getAttribute('aria-expanded') === 'true') {
          updateMobileNavigationHeight();
        }
      };

      window.addEventListener('resize', handleNavigationViewportChange, { passive: true });
      if (desktopNavigation.addEventListener) {
        desktopNavigation.addEventListener('change', handleNavigationViewportChange);
      } else {
        desktopNavigation.addListener(handleNavigationViewportChange);
      }
    }

    const revealItems = document.querySelectorAll('.reveal');
    if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const observer = new IntersectionObserver((entries, activeObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          activeObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

      revealItems.forEach((item) => observer.observe(item));
    } else {
      revealItems.forEach((item) => item.classList.add('is-visible'));
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const parallaxItems = [...document.querySelectorAll('[data-parallax]')];
    const progressItems = [...document.querySelectorAll('[data-scroll-progress]')];

    if (!reduceMotion && (parallaxItems.length || progressItems.length)) {
      let animationFrame = 0;

      const updateScrollEffects = () => {
        const viewportHeight = window.innerHeight;

        parallaxItems.forEach((item) => {
          const rect = item.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top > viewportHeight) return;
          const strength = Number(item.dataset.parallaxSpeed || item.dataset.parallax || 0.035);
          const distance = (rect.top + rect.height / 2 - viewportHeight / 2) * strength;
          item.style.setProperty('--parallax-y', `${distance.toFixed(2)}px`);
        });

        progressItems.forEach((item) => {
          const rect = item.getBoundingClientRect();
          const start = viewportHeight * 0.78;
          const end = Math.min(viewportHeight * 0.2, start - rect.height);
          const raw = (start - rect.top) / Math.max(1, start - end + rect.height);
          item.style.setProperty('--scroll-progress', Math.min(1, Math.max(0, raw)).toFixed(3));
        });

        animationFrame = 0;
      };

      const requestScrollEffects = () => {
        if (animationFrame) return;
        animationFrame = window.requestAnimationFrame(updateScrollEffects);
      };

      updateScrollEffects();
      window.addEventListener('scroll', requestScrollEffects, { passive: true });
      window.addEventListener('resize', requestScrollEffects, { passive: true });
    }

    document.querySelectorAll('[data-quantity]').forEach((quantity) => {
      const input = quantity.querySelector('input[type="number"]');
      const minus = quantity.querySelector('[data-quantity-minus]');
      const plus = quantity.querySelector('[data-quantity-plus]');
      if (!input) return;

      minus?.addEventListener('click', () => {
        input.value = String(Math.max(Number(input.min || 1), Number(input.value || 1) - 1));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      plus?.addEventListener('click', () => {
        input.value = String(Number(input.value || 1) + 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    document.querySelectorAll('[data-product-section]').forEach((section) => {
      const mainImage = section.querySelector('[data-product-main-image]');
      section.querySelectorAll('[data-product-thumb]').forEach((thumb) => {
        thumb.addEventListener('click', () => {
          if (!mainImage) return;
          const nextSource = thumb.getAttribute('data-image-src');
          if (nextSource) mainImage.src = nextSource;
          mainImage.removeAttribute('srcset');
          mainImage.alt = thumb.getAttribute('data-image-alt') || mainImage.alt;
        });
      });

      const variantSelect = section.querySelector('[data-variant-select]');
      const price = section.querySelector('[data-product-price] span');
      variantSelect?.addEventListener('change', () => {
        const option = variantSelect.options[variantSelect.selectedIndex];
        const nextPrice = option?.getAttribute('data-price');
        if (price && nextPrice) price.textContent = nextPrice;
      });
    });

    document.querySelectorAll('form[data-preview-form]').forEach((form) => {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const message = form.querySelector('[data-preview-success]');
        if (message) {
          message.hidden = false;
          message.focus();
        }
        form.reset();
      });
    });
  });
})();
